import React from 'react';
import { v4 as generateUuid } from 'uuid';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import Form from '@atlaskit/form';
import { PartnerProductCatalogueItem } from '@atlassian/cssxp-types-product';
import { PartnerQuoteCatalogueItem } from './partner-quote-catalog-items';
import { usePartnerCatalogueContext } from './context/partner-catalogue-context';
import { JiraRecommendations } from './jira-recommendations';
import { PartnerQuoteCatalogSelectApps } from './partner-quote-catalog-select-apps';
import { Divider, useFeatureToggle } from '@css-xp/base';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { isProductBelongsToJiraFamily } from '@css-xp/ppc-utils';
import './partner-quote-catalog.scss';
import { isPointAProduct } from './utils/is-pointa-product';

let formValueRef: any = {};

const JIRA_FAMILY_PRODUCT = 'jira-product';

export const PartnerQuoteCatalogue: React.FC = () => {
  const { quoteItems, setQuoteFormData } = usePartnerCatalogueContext();
  formValueRef = React.useRef<any>();
  const [estimates, setEstimates] = React.useState<Record<string, number>>({});
  const [selectedApps, setSelectedApps] = React.useState<any>({});

  const [productWiseUserCount, setProductWiseUserCount] = React.useState<Map<string, number>>(new Map());

  const [existingJiraProducts, setExistingJiraProducts] = React.useState<{ id: string }[]>([]);

  const [isEnabled] = useFeatureToggle();

  const isJPDAsJiraFamilyEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY);

  let formData = {};
  if (formValueRef.current) {
    formData = formValueRef.current()?.values;
  }

  React.useEffect(() => {
    if (formData) {
      setQuoteFormData(formData);
    }
  }, [JSON.stringify(formData)]);

  React.useEffect(() => {
    quoteItems.forEach((quoteItem) => {
      const newMap = new Map(productWiseUserCount);
      const isJiraFamily = isProductBelongsToJiraFamily(quoteItem.id, { isJPDAsJiraFamilyEnabled });
      if (isJiraFamily) {
        if (!newMap.has(JIRA_FAMILY_PRODUCT)) {
          newMap.set(JIRA_FAMILY_PRODUCT, 1);
        }
      } else if (!newMap.has(quoteItem.id)) {
        newMap.set(quoteItem.id, 1);
      }
      setProductWiseUserCount(newMap);
    });
  }, [quoteItems]);

  if (get(quoteItems, 'length') === 0) return null;

  const quoteItemClassifications = quoteItems.reduce(
    (
      previousValue: [
        {
          jira: PartnerProductCatalogueItem[];
          nonJira: PartnerProductCatalogueItem[];
        }
      ],
      currentValue: PartnerProductCatalogueItem
    ) => {
      if (isProductBelongsToJiraFamily(currentValue?.id, { isJPDAsJiraFamilyEnabled })) {
        previousValue[0].jira.push(currentValue);
      } else {
        previousValue[0].nonJira.push(currentValue);
      }
      return previousValue;
    },
    [{ jira: [], nonJira: [] }]
  );
  const formValue = formValueRef.current && formValueRef.current().values;
  const jiraCartItemId = generateUuid();
  const jiraNodes = [
    get(quoteItemClassifications, '0.jira.length') > 0 ? (
      <div className="quote-item partner-catalogue-quote-item" key={0}>
        {get(quoteItemClassifications, '0.jira').map((item: PartnerProductCatalogueItem, index: number) => {
          return (
            <>
              <PartnerQuoteCatalogueItem
                key={item?.id}
                product={item}
                index={index}
                setEstimates={setEstimates}
                estimates={estimates}
                setUserCountInMasterMap={(_id, numberOfUsers) => {
                  const newMasterUserCountRecord = new Map(productWiseUserCount);
                  newMasterUserCountRecord.set(JIRA_FAMILY_PRODUCT, numberOfUsers);
                  setProductWiseUserCount(newMasterUserCountRecord);
                }}
                parentFormRef={formValueRef}
                isJiraNode={true}
              />
              {/* We are adding some dividers between the Jira products */}
              {get(quoteItemClassifications, '0.jira.length') > 1 &&
                index !== get(quoteItemClassifications, '0.jira.length') - 1 && (
                  <Divider className="jira-product-divider" />
                )}
            </>
          );
        })}
        <JiraRecommendations existingJiraProductIds={existingJiraProducts} />

        <PartnerQuoteCatalogSelectApps
          selectedApps={selectedApps}
          setSelectedApps={setSelectedApps}
          jiraFamilyItems={defaultTo(get(quoteItemClassifications, '0.jira'), [])}
          index={0}
          offeringId={defaultTo(get(formValue, `plan.${get(quoteItemClassifications, '0.jira.0.id')}.value`), '')}
          product={get(quoteItemClassifications, '0.jira.0')}
          maxUserCount={productWiseUserCount.get(JIRA_FAMILY_PRODUCT) ?? 1}
          isJPDAsJiraFamilyEnabled={isJPDAsJiraFamilyEnabled}
        />

        {/*<PartnerQuoteCatalogPrice isJiraFamily={true} selectedApps={selectedApps} estimates={estimates} />*/}
      </div>
    ) : null,
  ];

  const nonJiraNodes = quoteItemClassifications[0].nonJira.map((item: PartnerProductCatalogueItem, index: number) => {
    return (
      <div className="quote-item partner-catalogue-quote-item" key={item?.id}>
        <PartnerQuoteCatalogueItem
          product={item}
          index={index + defaultTo(get(quoteItemClassifications, '0.jira.length'), 0)}
          setEstimates={setEstimates}
          estimates={estimates}
          setUserCountInMasterMap={(id, numberOfUsers) => {
            const newMasterUserCountRecord = new Map(productWiseUserCount);
            newMasterUserCountRecord.set(id, numberOfUsers);
            setProductWiseUserCount(newMasterUserCountRecord);
          }}
          parentFormRef={formValueRef}
          isJiraNode={false}
        />
        {!item?.isApp && !isPointAProduct(item.id) && (
          <PartnerQuoteCatalogSelectApps
            selectedApps={selectedApps}
            setSelectedApps={setSelectedApps}
            nonJiraFamilyItems={[item]}
            offeringId={defaultTo(get(formValue, `plan.${item.id}.value`), '')}
            product={item}
            index={index + defaultTo(get(quoteItemClassifications, '0.jira.length'), 0)}
            maxUserCount={productWiseUserCount.get(item.id) ?? 1}
          />
        )}

        {/*<PartnerQuoteCatalogPrice*/}
        {/*  estimates={estimates}*/}
        {/*  isJiraFamily={false}*/}
        {/*  selectedApps={selectedApps}*/}
        {/*  productId={item?.id}*/}
        {/*/>*/}
      </div>
    );
  });

  // @ts-ignore
  return (
    <>
      <div className="ppc-checkout-quote-catalog-container" data-testid="ppc-checkout-quote-catalog">
        {/* The submit CTA has been moved to the Catalogue summary footer, so we don't really need a onSubmit handler here.*/}
        {/* @ts-ignore */}
        <Form>
          {({ formProps, getState }) => {
            formValueRef.current = getState;
            return (
              <form {...formProps}>
                <div className="catalogue-quote-container">{[...jiraNodes, ...nonJiraNodes]}</div>
              </form>
            );
          }}
        </Form>
      </div>
    </>
  );
};
